import fishLogoBlue from '@/assets/fish-logo-blue.webp';
import fishLogoWhite from '@/assets/fish-logo-white.webp';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { Menu, X, Phone, Mail, ChevronDown } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';

interface DropdownItem {
  to: string;
  label: string;
}

interface NavItem {
  to: string;
  label: string;
  children?: DropdownItem[];
}

const navItems: NavItem[] = [
  { to: '/', label: 'Home' },
  {
    to: '/services',
    label: 'Our Services',
    children: [
      { to: '/services/minibus', label: 'Minibus Service' },
      { to: '/services/volunteer-drivers', label: 'Volunteer Drivers' },
      { to: '/services/home-visiting', label: 'Home Visiting' },
    ],
  },
  {
    to: '/get-involved',
    label: 'Get Involved',
    children: [
      { to: '/get-involved/volunteering', label: 'Volunteering' },
      { to: '/get-involved/donations', label: 'Donations' },
      { to: '/get-involved/legacies', label: 'Legacies' },
    ],
  },
  { to: '/about', label: 'About Us' },
  { to: '/news', label: 'Latest' },
];

function DesktopDropdown({ item }: { item: NavItem }) {
  const location = useLocation();
  const isChildActive = item.children?.some(c => location.pathname === c.to) ?? false;

  return (
    <div className="relative group">
      <Link
        to={item.to}
        className={`flex items-center gap-1 px-4 py-2 rounded-md text-base font-medium transition-colors ${
          isChildActive
            ? 'text-white underline underline-offset-4 decoration-2'
            : 'text-primary-foreground hover:bg-primary-foreground/10'
        }`}
      >
        {item.label}
        <ChevronDown className="w-4 h-4 transition-transform group-hover:rotate-180" />
      </Link>
      <div className="absolute left-0 top-full pt-1 invisible group-hover:visible group-focus-within:visible opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-all duration-150 z-50">
        <div className="bg-white rounded-md shadow-lg py-1 min-w-[200px] border border-border">
          {item.children!.map(child => (
            <Link
              key={child.to}
              to={child.to}
              className={`block px-4 py-2.5 text-sm font-medium transition-colors ${
                location.pathname === child.to
                  ? 'bg-primary/10 text-primary'
                  : 'text-foreground hover:bg-muted'
              }`}
            >
              {child.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function MobileDropdown({ item, onClose }: { item: NavItem; onClose: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const location = useLocation();

  return (
    <div>
      <div className="flex items-center">
        <Link
          to={item.to}
          onClick={onClose}
          className={`flex-1 px-4 py-3 rounded-l-md text-lg font-medium transition-colors ${
            location.pathname === item.to
              ? 'text-white underline underline-offset-4 decoration-2'
              : 'text-primary-foreground hover:bg-primary-foreground/10'
          }`}
        >
          {item.label}
        </Link>
        <button
          onClick={() => setExpanded(!expanded)}
          className="px-3 py-3 rounded-r-md text-primary-foreground hover:bg-primary-foreground/10"
          aria-expanded={expanded}
          aria-label={`Show ${item.label} sub-pages`}
        >
          <ChevronDown className={`w-5 h-5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {expanded && (
        <div className="ml-4 flex flex-col gap-0.5">
          {item.children!.map(child => (
            <Link
              key={child.to}
              to={child.to}
              onClick={onClose}
              className={`px-4 py-2.5 rounded-md text-base font-medium transition-colors ${
                location.pathname === child.to
                  ? 'text-white underline underline-offset-4 decoration-2'
                  : 'text-primary-foreground/80 hover:bg-primary-foreground/10'
              }`}
            >
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const { user, role, signOut } = useAuth();

  return (
    <header className="bg-primary text-primary-foreground shadow-lg sticky top-0 z-50">
      {/* Top bar */}
      <div className="bg-cerulean text-cerulean-foreground py-2 px-4 text-sm">
        <div className="container mx-auto flex flex-wrap gap-4 justify-center md:justify-end items-center">
          <a href="tel:01189723986" className="flex items-center gap-1 hover:underline font-medium text-base">
            <Phone className="w-4 h-4" /> 0118 972 3986
          </a>
          <a href="mailto:office@fishvolunteercentre.org.uk" className="flex items-center gap-1 hover:underline font-medium text-base">
            <Mail className="w-4 h-4" /> office@fishvolunteercentre.org.uk
          </a>
        </div>
      </div>

      {/* Main nav */}
      <div className="container mx-auto flex items-center justify-between py-4 px-4">
        <Link to="/" className="flex items-center gap-3 group">
          <div className="bg-white rounded-lg p-1.5">
            <img src={fishLogoBlue} alt="FISH Volunteer Centre logo" className="h-8 w-auto" />
          </div>
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="text-white font-bold text-lg">FISH Volunteer Centre</span>
            <span className="text-cerulean-foreground text-sm">Support & Transport for the Community</span>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1" aria-label="Main navigation">
          {navItems.map(item =>
            item.children ? (
              <DesktopDropdown key={item.to} item={item} />
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-md text-base font-medium transition-colors ${
                    isActive
                      ? 'text-white underline underline-offset-4 decoration-2'
                      : 'text-primary-foreground hover:bg-primary-foreground/10'
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
          <Link to="/contact" className="ml-2 px-4 py-2 rounded-md text-base font-semibold transition-colors bg-cerulean text-white hover:bg-cerulean/90">
            Contact Us
          </Link>
          {user && (
            <>
              {role === 'admin' && (
                <NavLink to="/portal/admin" className={({ isActive }) => `px-4 py-2 rounded-md text-base font-bold transition-colors ${isActive ? 'text-white underline underline-offset-4 decoration-2' : 'bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30'}`}>
                  Admin
                </NavLink>
              )}
              <Button variant="outline" size="sm" onClick={signOut} className="ml-2 border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10 bg-transparent">
                Sign Out
              </Button>
            </>
          )}
        </nav>

        {/* Mobile menu toggle */}
        <button
          className="lg:hidden p-2 rounded-md hover:bg-primary-foreground/10"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <nav className="lg:hidden px-4 pb-4 flex flex-col gap-1" aria-label="Mobile navigation">
          {navItems.map(item =>
            item.children ? (
              <MobileDropdown key={item.to} item={item} onClose={() => setOpen(false)} />
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-md text-lg font-medium transition-colors ${
                    isActive ? 'text-white underline underline-offset-4 decoration-2' : 'text-primary-foreground hover:bg-primary-foreground/10'
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
          <Link to="/contact" onClick={() => setOpen(false)} className="px-4 py-3 rounded-md text-lg font-semibold text-primary-foreground hover:bg-primary-foreground/10">Contact Us</Link>
          {user && (
            <>
              {role === 'admin' && <NavLink to="/portal/admin" onClick={() => setOpen(false)} className="px-4 py-3 rounded-md text-lg font-bold bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30">Admin Dashboard</NavLink>}
              <button onClick={() => { signOut(); setOpen(false); }} className="text-left px-4 py-3 rounded-md text-lg font-medium text-primary-foreground hover:bg-primary-foreground/10">Sign Out</button>
            </>
          )}
        </nav>
      )}
    </header>
  );
}
